import React, { createContext, useContext, useState, ReactNode } from 'react';

interface Scene {
  startTime: number;
  endTime: number;
  clipPath: string;
}

interface ProgressState {
  stage: string;
  progress: number;
  message: string;
  details: string | null;
}

interface VideoState {
  file: File | null;
  preview: string | null;
  scenes: Scene[];
  isProcessing: boolean;
  progress: ProgressState;
  error: string | null;
  controller: AbortController | null;
  xhr: XMLHttpRequest | null;
}

interface VideoContextType {
  videoState: VideoState;
  setVideoState: React.Dispatch<React.SetStateAction<VideoState>>;
  resetVideoState: () => void;
  startSceneDetection: (file: File) => Promise<void>;
  stopSceneDetection: () => void;
  checkProcessingStatus: () => boolean;
}

const initialVideoState: VideoState = {
    file: null,
    preview: null,
    scenes: [],
    isProcessing: false,
    progress: {
      stage: '',
      progress: 0,
      message: '',
      details: null
    },
    error: null,
    controller: null,
    xhr: null
};

const VideoContext = createContext<VideoContextType | undefined>(undefined);

export const VideoProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [videoState, setVideoState] = useState<VideoState>(initialVideoState);

  const resetVideoState = () => {
    if (videoState.preview) {
      URL.revokeObjectURL(videoState.preview);
    }
    setVideoState(initialVideoState);
  };

  const startSceneDetection = async (file: File) => {
    try {
      setVideoState(prev => ({
        ...prev,
        isProcessing: true,
        progress: {
          stage: 'uploading',
          progress: 0,
          message: 'Uploading video...',
          details: `0 MB of ${(file.size / (1024 * 1024)).toFixed(1)} MB`
        },
        scenes: [],
        error: null,
        xhr: null
      }));

      const formData = new FormData();
      formData.append('video', file);

      const controller = new AbortController();
      setVideoState(prev => ({ ...prev, controller }));

      // Use XMLHttpRequest to track upload progress
      const xhr = new XMLHttpRequest();
      setVideoState(prev => ({ ...prev, xhr }));
      xhr.open('POST', `${import.meta.env.VITE_API_URL}/api/scene-detection/detect`);
      xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);

      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          // Upload progress is scaled to 20% of total progress
          const uploadProgress = Math.round((event.loaded / event.total) * 20);
          setVideoState(prev => ({
            ...prev,
            progress: {
              ...prev.progress,
              stage: 'uploading',
              progress: uploadProgress,
              message: 'Uploading video...',
              details: `${(event.loaded / (1024 * 1024)).toFixed(1)} MB of ${(event.total / (1024 * 1024)).toFixed(1)} MB`
            }
          }));
        }
      };

      // Handle response
      xhr.onload = () => {
        if (xhr.status === 200) {
          const responseText = xhr.responseText;
          const lines = responseText.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'progress') {
                  // Update progress based on the stage
                  let progress = data.progress.progress;
                  if (data.progress.stage === 'uploading') {
                    // Upload progress is scaled to 20% of total progress
                    progress = Math.round((progress / 100) * 20);
                  } else if (data.progress.stage === 'initializing') {
                    // Initializing is 20-30%
                    progress = 20 + Math.round((progress / 100) * 10);
                  } else if (data.progress.stage === 'detecting_scenes') {
                    // Detecting scenes is 30-50%
                    progress = 30 + Math.round((progress / 100) * 20);
                  } else if (data.progress.stage === 'processing_results') {
                    // Processing results is 50-70%
                    progress = 50 + Math.round((progress / 100) * 20);
                  } else if (data.progress.stage === 'extracting_clips') {
                    // Extracting clips is 70-90%
                    progress = 70 + Math.round((progress / 100) * 20);
                  } else if (data.progress.stage === 'finalizing') {
                    // Finalizing is 90-100%
                    progress = 90 + Math.round((progress / 100) * 10);
                  }

                setVideoState(prev => ({
                  ...prev,
                    progress: {
                      stage: data.progress.stage,
                      progress: progress,
                      message: data.progress.message,
                      details: data.progress.details
                    }
                }));
              } else if (data.type === 'complete') {
                setVideoState(prev => ({
                  ...prev,
                  isProcessing: false,
                  scenes: data.scenes,
                  progress: {
                    stage: 'complete',
                    progress: 100,
                    message: 'Scene detection complete',
                      details: `Found ${data.scenes.length} scenes`
                  },
                  controller: null,
                  xhr: null
                }));
              } else if (data.type === 'error') {
                setVideoState(prev => ({
                  ...prev,
                  isProcessing: false,
                  error: data.error,
                    progress: {
                      stage: 'error',
                      progress: 0,
                      message: 'Processing failed',
                      details: data.error
                    },
                    controller: null,
                    xhr: null
                  }));
                }
              } catch (parseError) {
                console.error('Error parsing SSE data:', parseError);
                setVideoState(prev => ({
                  ...prev,
                  isProcessing: false,
                  error: 'Error processing server response',
                  progress: {
                    stage: 'error',
                    progress: 0,
                    message: 'Processing failed',
                    details: 'Error processing server response'
                  },
                  controller: null,
                  xhr: null
                }));
              }
            }
          }
        } else {
          const errorData = JSON.parse(xhr.responseText);
          throw new Error(errorData.error || 'Failed to process video');
        }
      };

      // Handle SSE events
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 3) { // Loading state
          const responseText = xhr.responseText;
          const lines = responseText.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));

                if (data.type === 'progress') {
                  // Update progress based on the stage
                  let progress = data.progress.progress;
                  if (data.progress.stage === 'uploading') {
                    // Upload progress is scaled to 20% of total progress
                    progress = Math.round((progress / 100) * 20);
                  } else if (data.progress.stage === 'initializing') {
                    // Initializing is 20-30%
                    progress = 20 + Math.round((progress / 100) * 10);
                  } else if (data.progress.stage === 'detecting_scenes') {
                    // Detecting scenes is 30-50%
                    progress = 30 + Math.round((progress / 100) * 20);
                  } else if (data.progress.stage === 'processing_results') {
                    // Processing results is 50-70%
                    progress = 50 + Math.round((progress / 100) * 20);
                  } else if (data.progress.stage === 'extracting_clips') {
                    // Extracting clips is 70-90%
                    progress = 70 + Math.round((progress / 100) * 20);
                  } else if (data.progress.stage === 'finalizing') {
                    // Finalizing is 90-100%
                    progress = 90 + Math.round((progress / 100) * 10);
                  }

                  setVideoState(prev => ({
                    ...prev,
                    progress: {
                      stage: data.progress.stage,
                      progress: progress,
                      message: data.progress.message,
                      details: data.progress.details
                    }
                  }));
                }
              } catch (parseError) {
                console.error('Error parsing SSE data:', parseError);
              }
            }
          }
        }
      };

      xhr.onerror = () => {
        setVideoState(prev => ({
          ...prev,
          isProcessing: false,
          error: 'Network error occurred',
          progress: {
            stage: 'error',
            progress: 0,
            message: 'Processing failed',
            details: 'Network error occurred'
          },
          controller: null,
          xhr: null
        }));
      };

      xhr.send(formData);
    } catch (error) {
      console.error('Error in startSceneDetection:', error);
      setVideoState(prev => ({
        ...prev,
        isProcessing: false,
        error: error instanceof Error ? error.message : 'Failed to process video',
        progress: {
          stage: 'error',
          progress: 0,
          message: 'Processing failed',
          details: error instanceof Error ? error.message : 'Failed to process video'
        },
        controller: null,
        xhr: null
      }));
    }
  };

  const stopSceneDetection = () => {
    if (videoState.xhr) {
      videoState.xhr.abort();
    }
    if (videoState.controller) {
      videoState.controller.abort();
    }
    setVideoState(prev => ({
      ...prev,
      isProcessing: false,
      progress: {
        stage: '',
        progress: 0,
        message: '',
        details: null
      },
      controller: null,
      xhr: null
    }));
  };

  const checkProcessingStatus = () => {
    return videoState.isProcessing;
  };

  return (
    <VideoContext.Provider value={{ 
      videoState, 
      setVideoState, 
      resetVideoState,
      startSceneDetection,
      stopSceneDetection,
      checkProcessingStatus
    }}>
      {children}
    </VideoContext.Provider>
  );
};

export const useVideo = () => {
  const context = useContext(VideoContext);
  if (context === undefined) {
    throw new Error('useVideo must be used within a VideoProvider');
  }
  return context;
}; 

export default VideoContext; 